import React from 'react'

import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
    Divider,
} from '@mui/material'
import {
    HomeOutlined,
    CalendarToday,
    PersonOutline,
    Business,
    LocationOn,
    Group,
    ChatBubbleOutline,
    Notes,
    Email as EmailIcon,
} from '@material-ui/icons'
import { Badge, Button, IconButton, Theme } from '@material-ui/core'

import {
    AddPropertyForm,
    FormStatus,
    RemovePropertyForm,
} from '../../../models/Forms'
import { toMMDDYYYY } from '../../../helpers'
import { FormStatusBadge } from '../../../components/FormStatusBadge'
import { Container } from '../../../components'
import { FormStatusTransitionButtons, TransitionMethod } from './FormButtons'
import { UpdateFormRequest } from '../../../hooks/useAddRemovePropertyForms'

interface Props {
    form: AddPropertyForm
    theme: Theme
    handleOpenMessageDrawer: (form: AddPropertyForm) => void
    handleUpdateForm: (request: UpdateFormRequest) => Promise<void>
    onOpenOnboardModal: () => void
}

export const AddPropertyFormRow = ({
    form,
    theme,
    handleOpenMessageDrawer,
    handleUpdateForm,
    onOpenOnboardModal,
}: Props) => {
    const handleTransition: TransitionMethod = async (
        event: React.MouseEvent<HTMLButtonElement>,
        form: AddPropertyForm | RemovePropertyForm,
        status: FormStatus,
    ) => {
        await handleUpdateForm({
            id: form.id,
            body: { status },
        })
    }

    return (
        <Card
            elevation={1}
            sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                },
            }}
            style={{
                margin: theme.spacing(1),
            }}
        >
            <CardContent sx={{ p: 1, pb: '8px !important' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ fontWeight: 600 }}
                    >
                        {form.property_name}
                    </Typography>

                    <Container style={{ flex: 1 }} />

                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation()
                            handleOpenMessageDrawer(form)
                        }}
                        style={{ marginRight: theme.spacing(1) }}
                    >
                        <Badge
                            badgeContent={form.message_count}
                            color={
                                (form.message_count ?? 0) > 0
                                    ? 'primary'
                                    : 'secondary'
                            }
                        >
                            <ChatBubbleOutline />
                        </Badge>
                    </IconButton>

                    <FormStatusBadge
                        status={form.status}
                        transitions={form.transitions}
                        createdDate={form.created_date}
                    />
                </div>

                <Divider sx={{ mb: 1.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Business
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body1">
                                {form.organization.name}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <HomeOutlined
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body1">
                                {form.unit_count} Units
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 1,
                            }}
                        >
                            <LocationOn
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                    marginTop: 0.5,
                                }}
                            />
                            <Typography variant="body2">
                                {form.address.address1},<br />
                                {form.address.city}, {form.address.state}{' '}
                                {form.address.zip_code}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <PersonOutline
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Created By: {form.created_by.name}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <CalendarToday
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Created:{' '}
                                {toMMDDYYYY(new Date(form.created_date))}
                            </Typography>
                        </Box>
                        {form.start_date && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <CalendarToday
                                    style={{
                                        marginRight: 8,
                                        color: 'primary.main',
                                    }}
                                />
                                <Typography variant="body2">
                                    Start Date:{' '}
                                    {toMMDDYYYY(new Date(form.start_date))}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Group
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Regional Manager:
                                </Typography>
                                <Typography variant="body2">
                                    {form.regional_manager.name} (
                                    {form.regional_manager.email})
                                </Typography>
                            </Box>
                        </Box>
                        {form.property_manager && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <Group
                                    style={{
                                        marginRight: 8,
                                        color: 'primary.main',
                                    }}
                                />
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Property Manager:
                                    </Typography>
                                    <Typography variant="body2">
                                        {form.property_manager.name} (
                                        {form.property_manager.email})
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {form.other_users && form.other_users.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Group
                                    style={{
                                        marginRight: 8,
                                        color: 'primary.main',
                                    }}
                                />
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Other Users:
                                    </Typography>
                                    <Typography variant="body2">
                                        {form.other_users
                                            .map(
                                                (user) =>
                                                    `${user.name} (${user.email})`,
                                            )
                                            .join(', ')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {(form.notes || form.billing_email) && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Grid container spacing={2}>
                            {form.billing_email && (
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        <EmailIcon
                                            style={{
                                                marginRight: 8,
                                                color: 'primary.main',
                                            }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                Billing Email:
                                            </Typography>
                                            <Typography variant="body2">
                                                {form.billing_email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                            {form.notes && (
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            mb: 1,
                                        }}
                                    >
                                        <Notes
                                            style={{
                                                marginRight: 8,
                                                color: 'primary.main',
                                                marginTop: 0.5,
                                            }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                Notes:
                                            </Typography>
                                            <Typography variant="body2">
                                                {form.notes}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}

                <Container>
                    <FormStatusTransitionButtons
                        form={form}
                        style={{ marginBottom: 0 }}
                        handleTransition={handleTransition}
                    />
                    <Container style={{ flex: 1 }} />

                    {form.status === FormStatus.PENDING && (
                        <Button
                            onClick={() => {
                                onOpenOnboardModal()
                            }}
                            style={{
                                marginTop: 8,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                            }}
                        >
                            Onboard Apartment
                        </Button>
                    )}
                </Container>
            </CardContent>
        </Card>
    )
}
